import axios from 'axios';
import { currentEnvConfig } from '@/config/environmentsConfig';
import useApi from './api';
import useUserStore from '@/stores/user';

// Helper function to get JWT token from localStorage
function getJwtToken(): string | null {
  try {
    const userFromLocalStorage = localStorage.getItem('user');
    if (userFromLocalStorage) {
      const user = JSON.parse(userFromLocalStorage);
      return user.jwtToken || null;
    }
  } catch (error) {
    // Silently fail if localStorage is unavailable or corrupted
    console.error('Error getting JWT token from localStorage', error);
  }
  return null;
}

// Helper function to get Authorization header with JWT token
function getAuthHeaders(): Record<string, string> {
  const token = getJwtToken();
  if (token) {
    return {
      'Authorization': `Bearer ${token}`
    };
  }
  return {};
}

// Helper function to check if an error is a 401 Unauthorized
function isUnauthorizedError(error: any): boolean {
  return (
    error.response?.status === 401 ||
    error.status === 401 ||
    (error.message && (
      error.message.includes('401') ||
      error.message.toLowerCase().includes('unauthorized') ||
      error.message.toLowerCase().includes('token expired')
    ))
  );
}

// Helper function to refresh JWT token using the dedicated refresh endpoint
// Requires FormsAuthentication session cookie (withCredentials: true)
async function refreshJwtToken(): Promise<string | null> {
  try {
    const { post } = useApi();
    
    // POST to /Api/Auth/RefreshToken endpoint
    // withCredentials is already set globally in api.ts (axios.defaults.withCredentials = true)
    const { responseBody, response } = await post('Api/Auth/RefreshToken', {}, {
      isLogoutOnInvalidAuth: false // Don't logout on invalid auth for token refresh
    });
    
    // Response is JSON: { "token": "..." }
    if (response.status === 401) {
      // User not authenticated - token refresh failed
      console.error('User not authenticated - token refresh failed');
      return null;
    }
    
    if (responseBody?.token) {
      const newToken = responseBody.token;
      
      // Update token in store and localStorage
      const userStore = useUserStore();
      userStore.jwtToken = newToken;
      
      const userFromLocalStorage = localStorage.getItem('user');
      if (userFromLocalStorage) {
        const user = JSON.parse(userFromLocalStorage);
        user.jwtToken = newToken;
        localStorage.setItem('user', JSON.stringify(user));
      }
      
      return newToken;
    }
  } catch (error: any) {
    // Handle 401 or other errors
    if (error.response?.status === 401) {
      return null;
    }
  }
  return null;
}

// Currently playing content interface
export interface CurrentlyPlayingContent {
  type: string;
  feature: string;
  contentUrl: string;
  contentTitle: string;
}

// Legacy raw metrics interface (keeping for backward compatibility)
export interface MetricsData {
  id: string;
  player_uuid: string;
  account_id: number;
  event_type: string;
  mem_free_mb: number;
  mem_total_mb: number;
  cpu_usage_pct: number;
  cpu_count: number;
  disk_free_mb: number;
  disk_total_mb: number;
  temperature_c: number;
  currently_playing: CurrentlyPlayingContent[];
  created_at: string;
  sent_at: string;
}

// New aggregated metrics interface
export interface AggregatedMetricsData {
  time_bucket: string;
  player_uuid: string;
  avg_mem_usage_pct: number;
  avg_cpu_usage_pct: number;
  avg_disk_usage_pct: number;
  avg_temperature_c: number;
  max_mem_usage_pct: number;
  max_cpu_usage_pct: number;
  max_disk_usage_pct: number;
  max_temperature_c: number;
  data_points: number;
}

export interface AggregatedMetricsResponse {
  success: boolean;
  data: AggregatedMetricsData[];
  interval: string;
  totalBuckets: number;
  filters: {
    accountId: number;
    startTime: string;
    endTime: string;
    interval: string;
  };
}

export interface MetricsQueryParams {
  accountId?: number; // Primary filter - account ID
  startTime?: string; // Time range start
  endTime?: string;   // Time range end
  limit?: number;     // Pagination limit
  offset?: number;    // Pagination offset
  // Note: playerUuid filtering removed as per dashboard requirements
}

export interface AggregatedMetricsQueryParams {
  accountId: number;      // Required: Account ID to filter data
  startTime: string;      // Required: Start time in ISO 8601 format
  endTime: string;        // Required: End time in ISO 8601 format
  interval?: '1h' | '4h' | '6h' | '1d'; // Optional: Time bucket interval
  playerUuids?: string;   // Optional: Comma-separated list of player UUIDs
}

const METRICS_BASE_URL = currentEnvConfig?.metricsBaseUrl || 'http://localhost:3000';

export async function getMetrics(params: MetricsQueryParams): Promise<MetricsData[]> {
  try {
    const response = await axios.get(`${METRICS_BASE_URL}/api/v1/metrics/query`, {
      params,
      headers: getAuthHeaders(),
      withCredentials: false, // Since this is a local API
      timeout: 10000 // 10 second timeout
    });
    
    // The API returns {success: true, data: [...], pagination: {...}}
    if (!response.data || !response.data.success || !response.data.data) {
      return [];
    }
    
    const result = Array.isArray(response.data.data) ? response.data.data : [];
    return result;
  } catch (error: any) {
    // Handle 401 Unauthorized - token expired, try to refresh and retry
    if (isUnauthorizedError(error)) {
      const newToken = await refreshJwtToken();
      if (newToken) {
        // Retry the request with the new token
        const response = await axios.get(`${METRICS_BASE_URL}/api/v1/metrics/query`, {
          params,
          headers: getAuthHeaders(),
          withCredentials: false,
          timeout: 10000
        });
        
        if (!response.data || !response.data.success || !response.data.data) {
          return [];
        }
        
        return Array.isArray(response.data.data) ? response.data.data : [];
      } else {
        throw new Error('Your session has expired. Please refresh the page to continue.');
      }
    }
    
    // Provide more specific error messages
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(`Cannot connect to metrics API at ${METRICS_BASE_URL}. Please ensure the API server is running.`);
    } else if (error.code === 'ECONNABORTED') {
      // Timeout could also be due to auth issues, check for 401 first
      if (isUnauthorizedError(error)) {
        throw new Error('Your session has expired. Please refresh the page to continue.');
      }
      throw new Error('Metrics API request timed out. Please try again or refresh the page.');
    } else if (error.response?.status === 400) {
      const errorMsg = error.response?.data?.message || 'Invalid request parameters for metrics API.';
      throw new Error(`API validation error: ${errorMsg}`);
    } else if (error.response?.status >= 500) {
      throw new Error('Metrics API server error. Please try again later.');
    }
    
    // Check for 401 in generic fallback before showing connection error
    if (isUnauthorizedError(error)) {
      throw new Error('Your session has expired. Please refresh the page to continue.');
    }
    
    throw new Error('Failed to fetch metrics data. Please refresh the page and try again.');
  }
}

export async function getLatestMetrics(params: Pick<MetricsQueryParams, 'accountId' | 'limit'>): Promise<MetricsData[]> {
  try {
    const response = await axios.get(`${METRICS_BASE_URL}/api/v1/metrics/latest`, {
      params,
      headers: getAuthHeaders(),
      withCredentials: false,
      timeout: 10000
    });
    
    // The API returns {success: true, data: [...], pagination: {...}}
    if (!response.data || !response.data.success || !response.data.data) {
      return [];
    }
    
    const result = Array.isArray(response.data.data) ? response.data.data : [];
    return result;
  } catch (error: any) {
    // Handle 401 Unauthorized - token expired, try to refresh and retry
    if (isUnauthorizedError(error)) {
      const newToken = await refreshJwtToken();
      if (newToken) {
        // Retry the request with the new token
        const response = await axios.get(`${METRICS_BASE_URL}/api/v1/metrics/latest`, {
          params,
          headers: getAuthHeaders(),
          withCredentials: false,
          timeout: 10000
        });
        
        if (!response.data || !response.data.success || !response.data.data) {
          return [];
        }
        
        return Array.isArray(response.data.data) ? response.data.data : [];
      } else {
        throw new Error('Your session has expired. Please refresh the page to continue.');
      }
    }
    
    // Provide more specific error messages
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(`Cannot connect to metrics API at ${METRICS_BASE_URL}. Please ensure the API server is running.`);
    } else if (error.code === 'ECONNABORTED') {
      // Timeout could also be due to auth issues, check for 401 first
      if (isUnauthorizedError(error)) {
        throw new Error('Your session has expired. Please refresh the page to continue.');
      }
      throw new Error('Metrics API request timed out. Please try again or refresh the page.');
    } else if (error.response?.status === 400) {
      const errorMsg = error.response?.data?.message || 'Invalid request parameters for metrics API.';
      throw new Error(`API validation error: ${errorMsg}`);
    } else if (error.response?.status >= 500) {
      throw new Error('Metrics API server error. Please try again later.');
    }
    
    // Check for 401 in generic fallback before showing connection error
    if (isUnauthorizedError(error)) {
      throw new Error('Your session has expired. Please refresh the page to continue.');
    }
    
    throw new Error('Failed to fetch latest metrics data. Please refresh the page and try again.');
  }
}

// Fetch latest metrics for a specific player UUID (dashed format)
export async function getLatestMetricsByPlayerUuid(playerUuid: string, limit: number = 1): Promise<MetricsData[]> {
  try {
    const response = await axios.get(`${METRICS_BASE_URL}/api/v1/metrics/latest`, {
      params: {
        playerUuid,
        limit
      },
      headers: getAuthHeaders(),
      withCredentials: false,
      timeout: 10000
    });

    if (!response.data || !response.data.success || !response.data.data) {
      return [];
    }

    return Array.isArray(response.data.data) ? response.data.data : [];
  } catch (error: any) {
    // Handle 401 Unauthorized - token expired, try to refresh and retry
    if (isUnauthorizedError(error)) {
      const newToken = await refreshJwtToken();
      if (newToken) {
        // Retry the request with the new token
        const response = await axios.get(`${METRICS_BASE_URL}/api/v1/metrics/latest`, {
          params: {
            playerUuid,
            limit
          },
          headers: getAuthHeaders(),
          withCredentials: false,
          timeout: 10000
        });

        if (!response.data || !response.data.success || !response.data.data) {
          return [];
        }

        return Array.isArray(response.data.data) ? response.data.data : [];
      } else {
        throw new Error('Your session has expired. Please refresh the page to continue.');
      }
    }
    
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(`Cannot connect to metrics API at ${METRICS_BASE_URL}. Please ensure the API server is running.`);
    }
    
    // Check for 401 in generic fallback before showing connection error
    if (isUnauthorizedError(error)) {
      throw new Error('Your session has expired. Please refresh the page to continue.');
    }
    
    throw new Error(error?.message || 'Failed to fetch latest metrics for player. Please refresh the page and try again.');
  }
}

// New optimized aggregated metrics API function
export async function getAggregatedMetrics(params: AggregatedMetricsQueryParams): Promise<AggregatedMetricsResponse> {
  try {
    const response = await axios.get(`${METRICS_BASE_URL}/api/v1/metrics/aggregated`, {
      params,
      headers: getAuthHeaders(),
      withCredentials: false,
      timeout: 10000
    });
    
    if (!response.data || !response.data.success) {
      throw new Error('API returned unsuccessful response');
    }
    
    const result = response.data as AggregatedMetricsResponse;
    return result;
  } catch (error: any) {
    // Handle 401 Unauthorized - token expired, try to refresh and retry
    if (isUnauthorizedError(error)) {
      const newToken = await refreshJwtToken();
      if (newToken) {
        // Retry the request with the new token
        const response = await axios.get(`${METRICS_BASE_URL}/api/v1/metrics/aggregated`, {
          params,
          headers: getAuthHeaders(),
          withCredentials: false,
          timeout: 10000
        });
        
        if (!response.data || !response.data.success) {
          throw new Error('API returned unsuccessful response');
        }
        
        return response.data as AggregatedMetricsResponse;
      } else {
        throw new Error('Your session has expired. Please refresh the page to continue.');
      }
    }
    
    // Provide more specific error messages
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(`Cannot connect to metrics API at ${METRICS_BASE_URL}. Please ensure the API server is running.`);
    } else if (error.code === 'ECONNABORTED') {
      // Timeout could also be due to auth issues, check for 401 first
      if (isUnauthorizedError(error)) {
        throw new Error('Your session has expired. Please refresh the page to continue.');
      }
      throw new Error('Metrics API request timed out. Please try again or refresh the page.');
    } else if (error.response?.status === 400) {
      const errorMsg = error.response?.data?.message || 'Invalid request parameters for aggregated metrics API.';
      throw new Error(`API validation error: ${errorMsg}`);
    } else if (error.response?.status >= 500) {
      throw new Error('Metrics API server error. Please try again later.');
    }
    
    // Check for 401 in generic fallback before showing connection error
    if (isUnauthorizedError(error)) {
      throw new Error('Your session has expired. Please refresh the page to continue.');
    }
    
    throw new Error('Failed to fetch aggregated metrics data. Please refresh the page and try again.');
  }
}

// Helper function to determine optimal interval based on date range
export function getOptimalInterval(startDate: string, endDate: string): '1h' | '4h' | '6h' | '1d' {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const durationHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
  
  // Single-day and near-single-day: prefer 4-hour buckets
  if (durationHours <= 30) {
    return '4h';
  } else if (durationHours <= 48) {
    return '1h';   // Up to 2 days: hourly data
  } else if (durationHours <= 168) {
    return '6h';   // Last week: 6-hour data
  } else {
    return '1d';   // More than a week: daily data
  }
}

// Helper function to transform metrics data for heatmap visualization
// Takes account-filtered metrics and transforms them for visualization
// Each data point represents one metric reading from one device at one point in time
export function transformMetricsForHeatmap(
  metrics: MetricsData[], 
  metricType: 'mem' | 'cpu' | 'disk' | 'temp'
): Array<{
  timestamp: string;
  playerUuid: string;
  value: number;
}> {
  const transformed = metrics.map(metric => ({
    timestamp: metric.sent_at, // Use sent_at as timestamp
    playerUuid: metric.player_uuid, // Keep player_uuid - heatmap shows multiple devices
    value: getMetricValue(metric, metricType)
  }));
  
  return transformed;
}

// New transform function for aggregated metrics data
export function transformAggregatedMetricsForHeatmap(
  aggregatedMetrics: AggregatedMetricsData[], 
  metricType: 'mem' | 'cpu' | 'disk' | 'temp'
): Array<{
  timestamp: string;
  playerUuid: string;
  value: number;
}> {
  const transformed = aggregatedMetrics.map(metric => ({
    timestamp: metric.time_bucket, // Use time_bucket as timestamp
    playerUuid: metric.player_uuid, // Keep player_uuid - heatmap shows multiple devices
    value: getAggregatedMetricValue(metric, metricType)
  }));
  
  return transformed;
}

function getMetricValue(metric: MetricsData, type: 'mem' | 'cpu' | 'disk' | 'temp'): number {
  switch (type) {
    case 'mem': {
      // Calculate memory usage percentage: (total - free) / total * 100
      const memUsed = metric.mem_total_mb - metric.mem_free_mb;
      return (memUsed / metric.mem_total_mb) * 100;
    }
    case 'cpu':
      return metric.cpu_usage_pct;
    case 'disk': {
      // Calculate disk usage percentage: (total - free) / total * 100
      const diskUsed = metric.disk_total_mb - metric.disk_free_mb;
      return (diskUsed / metric.disk_total_mb) * 100;
    }
    case 'temp':
      return metric.temperature_c;
    default:
      return 0;
  }
}

function getAggregatedMetricValue(metric: AggregatedMetricsData, type: 'mem' | 'cpu' | 'disk' | 'temp'): number {
  switch (type) {
    case 'mem':
      return metric.avg_mem_usage_pct;
    case 'cpu':
      return metric.avg_cpu_usage_pct;
    case 'disk':
      return metric.avg_disk_usage_pct;
    case 'temp':
      return metric.avg_temperature_c;
    default:
      return 0;
  }
}
